/* ============================================================
   DATECK — Shopify chrome (Empire 12.3.0 compatible)
   - Real Shopify AJAX cart (/cart.js, /cart/add.js, /cart/change.js)
   - Predictive search via /search/suggest.json
   - Mega menu, mobile drawer, promo bar carousel
   - Wishlist (localStorage), Recently Viewed, Quick view
   - Free-shipping progress bar
   - Click delegation for [data-dk-*]
   Single IIFE. No build step. No deps. Coexists with window.Theme.
   ============================================================ */
(function () {
  'use strict';

  const DK = window.DK || (window.DK = {});
  const I = (name, attrs = '') => {
    const lib = window.DK_ICONS || {};
    const svg = lib[name];
    if (!svg) return '';
    return svg.replace('<svg', `<svg ${attrs}`);
  };

  // Cache: live Liquid-rendered data is on window.DK_PRODUCTS / DK_DEPARTMENTS
  // (parent agent will populate these with real Shopify data).
  const PRODUCTS = () => window.DK_PRODUCTS || [];
  const DEPARTMENTS = () => window.DK_DEPARTMENTS || [];

  // -------------------------------------------------------- Utilities
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const fmt = (cents) => {
    const n = (cents || 0) / 100;
    try {
      return n.toLocaleString(undefined, {
        style: 'currency',
        currency: (DK.cart && DK.cart.state && DK.cart.state.currency) || 'USD',
      });
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  };
  const json = async (url, opts = {}) => {
    const r = await fetch(url, {
      ...opts,
      headers: {
        Accept: 'application/json',
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(opts.headers || {}),
      },
    });
    return r;
  };
  const lockScroll = (lock) => {
    if (lock) {
      document.body.dataset.dkScrollY = String(window.scrollY);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };
  const escapeHtml = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // -------------------------------------------------------- Cart layer
  DK.cart = {
    state: { items: [], total_price: 0, item_count: 0, currency: 'USD', original_total_price: 0 },
    listeners: [],
    _inflight: null,

    on(fn) { if (typeof fn === 'function') this.listeners.push(fn); },
    off(fn) { this.listeners = this.listeners.filter((f) => f !== fn); },
    emit() {
      this.listeners.forEach((fn) => { try { fn(this.state); } catch (e) { /* noop */ } });
      // Also broadcast a DOM event so other scripts (Empire) and template snippets can listen.
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: this.state }));
    },

    async refresh() {
      if (this._inflight) return this._inflight;
      this._inflight = (async () => {
        try {
          const r = await json('/cart.js');
          if (r.ok) {
            this.state = await r.json();
            this.emit();
          }
        } catch (e) { /* offline-safe */ }
        finally { this._inflight = null; }
        return this.state;
      })();
      return this._inflight;
    },

    async add(variantId, qty = 1, properties = {}) {
      if (!variantId) return null;
      try {
        const r = await json('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ id: variantId, quantity: qty, properties }),
        });
        if (!r.ok) {
          let err = {};
          try { err = await r.json(); } catch (e) { /* noop */ }
          DK.toast(err.message || 'Could not add to cart', err.description || '', 'error');
          throw new Error(err.description || err.message || 'add failed');
        }
        const data = await r.json();
        await this.refresh();
        DK.toast('Added to cart', data.product_title || '');
        DK.openCart();
        return data;
      } catch (e) {
        // toast already shown above for fetch failures
        return null;
      }
    },

    async change(line, qty) {
      // line is 1-indexed
      try {
        const r = await json('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ line, quantity: qty }),
        });
        if (!r.ok) {
          let err = {};
          try { err = await r.json(); } catch (e) { /* noop */ }
          DK.toast(err.message || 'Could not update cart', err.description || '', 'error');
          return this.state;
        }
        this.state = await r.json();
        this.emit();
        return this.state;
      } catch (e) {
        return this.state;
      }
    },

    async remove(line) { return this.change(line, 0); },

    async clear() {
      try {
        const r = await json('/cart/clear.js', { method: 'POST', body: '{}' });
        if (r.ok) { this.state = await r.json(); this.emit(); }
      } catch (e) { /* noop */ }
      return this.state;
    },

    count() { return this.state.item_count || 0; },
    subtotal() { return (this.state.total_price || 0) / 100; },
    savings() {
      return (this.state.items || []).reduce((a, it) =>
        a + ((it.original_line_price || it.final_line_price || 0) - (it.final_line_price || 0)), 0) / 100;
    },
  };

  // -------------------------------------------------------- Toast
  function ensureToastStack() {
    let s = $('.dk-toast-stack');
    if (!s) {
      s = document.createElement('div');
      s.className = 'dk-toast-stack';
      document.body.appendChild(s);
    }
    return s;
  }
  DK.toast = function (title, sub = '', variant = '') {
    const s = ensureToastStack();
    const el = document.createElement('div');
    el.className = 'dk-toast ' + (variant || '');
    el.setAttribute('role', 'status');
    el.innerHTML = `${I('check', 'class="icon" width="16" height="16"')}
      <div>
        <div class="dk-toast-title">${escapeHtml(title)}</div>
        ${sub ? `<div class="dk-toast-sub">${escapeHtml(sub)}</div>` : ''}
      </div>`;
    s.appendChild(el);
    requestAnimationFrame(() => el.classList.add('in'));
    setTimeout(() => { el.classList.remove('in'); el.classList.add('out'); }, 2200);
    setTimeout(() => { el.remove(); }, 2700);
  };

  // -------------------------------------------------------- Cart drawer
  function freeShipBlock() {
    const sub = DK.cart.subtotal();
    const threshold = Number(document.body.dataset.dkFreeship || 250);
    const progress = Math.min(100, threshold > 0 ? (sub / threshold) * 100 : 0);
    const remaining = Math.max(0, threshold - sub);
    if (!DK.cart.state.items || DK.cart.state.items.length === 0) return '';
    return `
      <div class="dk-cart-ship" data-dk-shipprog>
        <div class="dk-cart-ship-row">
          <span>${remaining > 0
            ? `Add <strong>${fmt(remaining * 100)}</strong> for free U.S. shipping ($5.50 handling applies)`
            : 'You qualify for free U.S. shipping ($5.50 handling applies)'}</span>
          <span>${fmt(sub * 100)} / ${fmt(threshold * 100)}</span>
        </div>
        <div class="dk-cart-ship-bar"><div style="width:${progress}%"></div></div>
      </div>`;
  }

  function cartLineHTML(it, idx) {
    const line = idx + 1;
    const img = it.image ? `<img src="${it.image}" alt="" loading="lazy" />` : '';
    const brand = it.vendor ? `<div class="dk-cart-line-brand">${escapeHtml(it.vendor)}</div>` : '';
    const variant = it.variant_title && it.variant_title !== 'Default Title'
      ? `<div class="dk-cart-line-variant">${escapeHtml(it.variant_title)}</div>` : '';
    const onSale = it.original_line_price && it.original_line_price > it.final_line_price;
    return `
      <div class="dk-cart-line" data-line="${line}">
        <div class="dk-cart-line-img">${img}</div>
        <div class="dk-cart-line-info">
          ${brand}
          <a class="dk-cart-line-name" href="${it.url || '#'}">${escapeHtml(it.product_title || it.title || '')}</a>
          ${variant}
          <div class="dk-cart-line-price">
            <span class="now">${fmt(it.final_line_price)}</span>
            ${onSale ? `<span class="was">${fmt(it.original_line_price)}</span>` : ''}
          </div>
          <div class="dk-cart-line-qty">
            <button type="button" data-dk-change="${line}" data-value="${it.quantity - 1}" aria-label="Decrease quantity">−</button>
            <span>${it.quantity}</span>
            <button type="button" data-dk-change="${line}" data-value="${it.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <div class="dk-cart-line-right">
          <button type="button" class="dk-cart-line-remove" data-dk-remove="${line}">Remove</button>
        </div>
      </div>`;
  }

  function cartDrawerHTML() {
    const items = DK.cart.state.items || [];
    const count = DK.cart.count();
    return `
      <div class="dk-drawer-backdrop" data-dk-cart-close></div>
      <aside class="dk-cart-drawer" role="dialog" aria-label="Cart" aria-modal="true">
        <div class="dk-drawer-header">
          <div class="dk-drawer-title">Your Cart <span class="dk-muted">(${count})</span></div>
          <button type="button" class="dk-drawer-close" data-dk-cart-close aria-label="Close cart">
            ${I('x', 'width="18" height="18"')}
          </button>
        </div>
        ${freeShipBlock()}
        <div class="dk-drawer-body">
          ${items.length === 0 ? `
            <div class="dk-cart-empty">
              ${I('bag', 'width="56" height="56"')}
              <div class="dk-cart-empty-title">Your cart is empty</div>
              <div class="dk-cart-empty-sub">Add gear and we'll keep it here for you.</div>
            </div>` : items.map(cartLineHTML).join('')}
        </div>
        <div class="dk-drawer-footer">
          ${items.length ? `
            <div class="dk-cart-subtotal">
              <span>Subtotal</span>
              <span>${fmt(DK.cart.state.total_price)}</span>
            </div>
            <a href="/checkout" class="dk-btn dk-btn-primary dk-btn-lg dk-btn-block">
              Checkout — ${fmt(DK.cart.state.total_price)}
            </a>
            <a href="/cart" class="dk-btn dk-btn-ghost dk-btn-block">View full cart</a>
          ` : `<a href="/collections/all" class="dk-btn dk-btn-primary dk-btn-lg dk-btn-block">Browse products</a>`}
        </div>
      </aside>`;
  }

  // The cart drawer is rendered by dateck-cart-drawer.liquid as <aside class="dk-cart-drawer">.
  // We do NOT mount a separate JS host — we operate on the Liquid element directly.
  function getCartDrawer() { return $('.dk-cart-drawer'); }

  // Re-render the LINES (and footer) inside the Liquid drawer when cart updates.
  function rerenderCartDrawerLines() {
    const drawer = getCartDrawer();
    if (!drawer) return;
    const linesContainer = drawer.querySelector('[data-dk-cart-drawer-lines]');
    if (!linesContainer) return;
    const items = DK.cart.state.items || [];
    if (items.length === 0) {
      linesContainer.innerHTML = `
        <div class="dk-cart-drawer__empty">
          <div class="dk-cart-drawer__empty-icon">${I('bag', 'width="56" height="56"')}</div>
          <h3>Your cart is empty</h3>
          <p>Find your gear in the catalog.</p>
          <a href="/collections" class="btn btn-primary btn-lg">Browse products</a>
        </div>`;
    } else {
      linesContainer.innerHTML = items.map((it, idx) => {
        const line = idx + 1;
        const onSale = it.original_line_price > it.final_line_price;
        return `
          <div class="dk-cart-line">
            <a href="${escapeHtml(it.url)}" class="dk-cart-line__img">
              ${(it.image || (it.featured_image && it.featured_image.url)) ? `<img src="${escapeHtml(it.image || it.featured_image.url)}" alt="${escapeHtml(it.product_title)}" loading="lazy">` : ''}
            </a>
            <div class="dk-cart-line__info">
              <div class="dk-cart-line__brand">${escapeHtml(it.vendor || '')}</div>
              <a href="${escapeHtml(it.url)}" class="dk-cart-line__name">${escapeHtml(it.product_title)}</a>
              ${it.variant_title && it.variant_title !== 'Default Title' ? `<div class="dk-cart-line__opts">${escapeHtml(it.variant_title)}</div>` : ''}
              <div class="dk-cart-line__qty">
                <button type="button" data-dk-change="${line}" data-value="${it.quantity - 1}" aria-label="Decrease">−</button>
                <span>${it.quantity}</span>
                <button type="button" data-dk-change="${line}" data-value="${it.quantity + 1}" aria-label="Increase">+</button>
              </div>
            </div>
            <div class="dk-cart-line__right">
              ${onSale ? `<span class="dk-cart-line__was">${fmt(it.original_line_price)}</span>` : ''}
              <span class="dk-cart-line__price">${fmt(it.final_line_price)}</span>
              <button type="button" class="dk-cart-line__remove" data-dk-remove="${line}" aria-label="Remove">${I('trash', 'width="14" height="14"')}</button>
            </div>
          </div>`;
      }).join('');
    }
    // Subtotal in footer
    const sub = drawer.querySelector('[data-dk-cart-drawer-subtotal]');
    if (sub) sub.textContent = fmt(DK.cart.state.total_price);
    const dcount = drawer.querySelector('[data-dk-cart-drawer-count]');
    if (dcount) dcount.textContent = '(' + DK.cart.count() + ')';
    const foot = drawer.querySelector('[data-dk-cart-drawer-foot]');
    if (foot) foot.style.display = items.length ? '' : 'none';
    // Free-ship progress
    const threshold = Number(drawer.dataset.dkFreeship || 250);
    const shipText = drawer.querySelector('[data-dk-shipprog-text]');
    const shipBar = drawer.querySelector('[data-dk-shipprog-bar]');
    const sub2 = DK.cart.subtotal();
    const remaining = Math.max(0, threshold - sub2);
    const pct = Math.min(100, threshold > 0 ? (sub2 / threshold) * 100 : 0);
    if (shipText) shipText.innerHTML = remaining > 0
      ? `${I('truck', 'width="14" height="14"')} Add <strong>${fmt(remaining * 100)}</strong> for free U.S. shipping ($5.50 handling applies)`
      : '🎉 You qualify for free U.S. shipping ($5.50 handling applies)';
    if (shipBar) shipBar.style.width = pct + '%';
  }

  DK.openCart = function () {
    const drawer = getCartDrawer();
    if (!drawer) return;
    rerenderCartDrawerLines();
    requestAnimationFrame(() => {
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll(true);
    });
  };
  DK.closeCart = function () {
    const drawer = getCartDrawer();
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);
  };

  // Update the header cart count badge + subtotal when cart changes
  function updateHeaderCart() {
    $$('[data-dk-cart-count]').forEach((el) => { el.textContent = DK.cart.count(); });
    $$('[data-dk-cart-subtotal]').forEach((el) => { el.textContent = fmt(DK.cart.state.total_price); });
    // Re-render the drawer lines (whether open or closed — cheap, keeps state fresh)
    rerenderCartDrawerLines();
    const drawer = getCartDrawer();
    // Free-ship bar — refresh on-page shipprog elements OUTSIDE the drawer (the cart page has one too)
    $$('[data-dk-shipprog]').forEach((el) => {
      // Only touch ones outside the drawer to avoid double-paint
      if (drawer && drawer.contains(el)) return;
      const threshold = Number(document.body.dataset.dkFreeship || 250);
      const sub = DK.cart.subtotal();
      const progress = Math.min(100, threshold > 0 ? (sub / threshold) * 100 : 0);
      const remaining = Math.max(0, threshold - sub);
      const txt = el.querySelector('[data-dk-shipprog-text]');
      const bar = el.querySelector('[data-dk-shipprog-bar]');
      if (txt) txt.textContent = remaining > 0
        ? `Add ${fmt(remaining * 100)} for free U.S. shipping ($5.50 handling applies)`
        : 'You qualify for free U.S. shipping ($5.50 handling applies)';
      if (bar) bar.style.width = progress + '%';
    });
  }

  // -------------------------------------------------------- Search overlay (predictive)
  function searchOverlayHTML() {
    const depts = DEPARTMENTS();
    return `
      <div class="dk-search-backdrop" data-dk-search-close></div>
      <div class="dk-search-panel" role="dialog" aria-label="Search" aria-modal="true">
        <div class="dk-search-head">
          <div class="dk-search-bar">
            ${I('search', 'width="20" height="20"')}
            <input id="dk-search-input" type="text"
              placeholder="Search the catalog — radios, dash cams, smart locks…" autocomplete="off" />
            <button type="button" class="dk-search-close" data-dk-search-close aria-label="Close search">
              ${I('x', 'width="20" height="20"')}
            </button>
          </div>
          <div class="dk-search-chips">
            ${['radio', 'dash cam', 'smart lock', 'rugged tablet', 'power station', 'GMRS'].map((t) =>
              `<button type="button" class="dk-chip" data-dk-search-term="${escapeHtml(t)}">${escapeHtml(t)}</button>`).join('')}
          </div>
        </div>
        <div class="dk-search-content">
          <aside class="dk-search-side">
            <h6>Browse by department</h6>
            <ul>
              ${depts.length
                ? depts.map((d) =>
                  `<li><a href="${escapeHtml(d.url || '/collections/' + (d.handle || d.id || ''))}">${escapeHtml(d.name || d.title || '')}</a></li>`
                ).join('')
                : '<li><a href="/collections/all">All products</a></li>'}
            </ul>
          </aside>
          <div class="dk-search-main">
            <div class="dk-search-empty" id="dk-search-empty">
              <h6>Start typing to see results…</h6>
            </div>
            <div class="dk-search-results" id="dk-search-results" hidden>
              <section data-section="products"></section>
              <section data-section="collections"></section>
              <section data-section="articles"></section>
            </div>
          </div>
        </div>
      </div>`;
  }

  const quoteSearchCache = new Map();
  let _searchAbort = null;
  let _searchDebounce = null;
  async function suggest(q) {
    if (!q || q.length < 2) return null;
    if (_searchAbort) { try { _searchAbort.abort(); } catch (e) { /* noop */ } }
    _searchAbort = new AbortController();
    const url = `/search/suggest.json?q=${encodeURIComponent(q)}` +
      `&resources[type]=product,collection,article` +
      `&resources[limit]=8` +
      `&resources[options][fields]=title,vendor,product_type,variants.sku`;
    try {
      const r = await fetch(url, { headers: { Accept: 'application/json' }, signal: _searchAbort.signal });
      if (!r.ok) return null;
      const data = await r.json();
      await Promise.all((data.resources?.results?.products || []).map(async p => {
        if (!/^(icom|ijkp)$/i.test(p.vendor || '') || !p.handle) return;
        try {
          if (!quoteSearchCache.has(p.handle)) {
            const detail = await fetch('/products/' + encodeURIComponent(p.handle) + '.js', {signal: _searchAbort.signal});
            if (!detail.ok) return;
            const product = await detail.json();
            quoteSearchCache.set(p.handle, (product.tags || []).includes('Icom quote required'));
          }
          if (quoteSearchCache.get(p.handle)) p.price = 'Request pricing';
        } catch (e) { /* A cancelled search is replaced by the next query. */ }
      }));
      return data;
    } catch (e) { return null; }
  }

  function renderSearchResults(data) {
    const wrap = $('#dk-search-results');
    const empty = $('#dk-search-empty');
    if (!wrap || !empty) return;
    const r = (data && data.resources && data.resources.results) || {};
    const products = (r.products || []).slice(0, 8);
    const collections = (r.collections || []).slice(0, 4);
    const articles = (r.articles || []).slice(0, 4);
    const hasAny = products.length || collections.length || articles.length;

    if (!hasAny) {
      empty.hidden = false;
      empty.innerHTML = '<h6>No matches — try a broader term.</h6>';
      wrap.hidden = true;
      return;
    }
    empty.hidden = true;
    wrap.hidden = false;

    wrap.querySelector('[data-section="products"]').innerHTML = products.length ? `
      <h6 class="dk-search-sec-title">Products</h6>
      <div class="dk-search-products">
        ${products.map((p) => `
          <a class="dk-search-product" href="${escapeHtml(p.url || '#')}">
            <div class="dk-search-product-img">${p.image ? `<img src="${escapeHtml(p.image)}" alt="" loading="lazy" />` : ''}</div>
            <div>
              ${p.vendor ? `<div class="dk-search-product-vendor">${escapeHtml(p.vendor)}</div>` : ''}
              <div class="dk-search-product-title">${escapeHtml(p.title || '')}</div>
              <div class="dk-search-product-price">${escapeHtml(p.price || '')}</div>
            </div>
          </a>`).join('')}
      </div>` : '';

    wrap.querySelector('[data-section="collections"]').innerHTML = collections.length ? `
      <h6 class="dk-search-sec-title">Collections</h6>
      <ul class="dk-search-list">
        ${collections.map((c) => `<li><a href="${escapeHtml(c.url || '#')}">${escapeHtml(c.title || '')}</a></li>`).join('')}
      </ul>` : '';

    wrap.querySelector('[data-section="articles"]').innerHTML = articles.length ? `
      <h6 class="dk-search-sec-title">Articles</h6>
      <ul class="dk-search-list">
        ${articles.map((a) => `<li><a href="${escapeHtml(a.url || '#')}">${escapeHtml(a.title || '')}</a></li>`).join('')}
      </ul>` : '';
  }

  function mountSearch() {
    // Operate on the Liquid-rendered <div class="dk-search-overlay">.
    const host = $('.dk-search-overlay');
    if (!host) return null;
    if (!host.dataset.wired) {
      // Wire input + chip clicks ONCE.
      const input = host.querySelector('[data-dk-search-input]');
      const results = host.querySelector('[data-dk-search-results]');
      if (input) {
        input.addEventListener('input', (e) => {
          const q = e.target.value;
          clearTimeout(_searchDebounce);
          _searchDebounce = setTimeout(async () => {
            if (!results) return;
            if (q && q.length >= 2) {
              const data = await suggest(q);
              if (data) {
                // Render results into [data-dk-search-results]
                const products = (data.resources?.results?.products) || [];
                const collections = (data.resources?.results?.collections) || [];
                const articles = (data.resources?.results?.articles) || [];
                results.innerHTML = `
                  ${products.length ? `<div class="dk-search-section"><h6>Products</h6><div class="dk-search-products">${products.slice(0,8).map(p => `
                    <a href="${escapeHtml(p.url || '#')}" class="dk-search-product">
                      ${p.featured_image && p.featured_image.url ? `<img src="${escapeHtml(p.featured_image.url)}" alt="" loading="lazy">` : ''}
                      <div><div class="dk-search-product__title">${escapeHtml(p.title || '')}</div><div class="dk-search-product__price">${escapeHtml(p.price || '')}</div></div>
                    </a>`).join('')}</div></div>` : ''}
                  ${collections.length ? `<div class="dk-search-section"><h6>Collections</h6>${collections.slice(0,4).map(c => `<a href="${escapeHtml(c.url || '#')}" class="dk-search-link">${escapeHtml(c.title || '')}</a>`).join('')}</div>` : ''}
                  ${articles.length ? `<div class="dk-search-section"><h6>Articles</h6>${articles.slice(0,4).map(a => `<a href="${escapeHtml(a.url || '#')}" class="dk-search-link">${escapeHtml(a.title || '')}</a>`).join('')}</div>` : ''}
                  ${products.length === 0 && collections.length === 0 && articles.length === 0 ? `<p class="dk-search-empty-msg">No matches for "${escapeHtml(q)}".</p>` : ''}
                `;
              }
            } else {
              results.innerHTML = '';
            }
          }, 180);
        });
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            const v = e.target.value.trim();
            if (v) window.location.href = '/search?q=' + encodeURIComponent(v);
          }
        });
      }
      host.querySelectorAll('[data-dk-search-term]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const t = btn.dataset.dkSearchTerm;
          if (input) { input.value = t; input.dispatchEvent(new Event('input', { bubbles: true })); input.focus(); }
        });
      });
      host.dataset.wired = '1';
    }
    return host;
  }

  DK.openSearch = function () {
    const host = mountSearch();
    if (!host) return;
    requestAnimationFrame(() => {
      host.classList.add('open');
      host.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      setTimeout(() => host.querySelector('[data-dk-search-input]')?.focus(), 80);
    });
  };
  DK.closeSearch = function () {
    const host = $('.dk-search-overlay');
    if (host) {
      host.classList.remove('open');
      host.setAttribute('aria-hidden', 'true');
    }
    lockScroll(false);
  };

  // -------------------------------------------------------- Mega menu
  // Buttons with [data-dk-mega="<id>"] anywhere on the page (typically inside .dk-catnav).
  // Content is sourced from <template data-dk-mega-content="<id>">…</template> in the DOM.
  function initMega() {
    const triggers = $$('[data-dk-mega]');
    if (!triggers.length) return;
    const panel = $('.dk-megamenu') || (() => {
      const p = document.createElement('div');
      p.className = 'dk-megamenu';
      const nav = $('.dk-catnav') || document.body;
      nav.appendChild(p);
      return p;
    })();

    let openKey = null;
    let closeTimer = null;

    const open = (key) => {
      const tpl = document.querySelector(`[data-dk-mega-content="${CSS.escape(key)}"]`);
      panel.innerHTML = tpl ? (tpl.innerHTML || tpl.textContent || '') : '';
      panel.classList.add('open');
      openKey = key;
    };
    const close = () => {
      panel.classList.remove('open');
      openKey = null;
    };

    triggers.forEach((btn) => {
      btn.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        open(btn.dataset.dkMega);
      });
      btn.addEventListener('focus', () => open(btn.dataset.dkMega));
      btn.addEventListener('click', (e) => {
        // If the trigger is an anchor (e.g. catnav department link), let it navigate.
        // If it's a button, toggle the panel.
        if (btn.tagName === 'A' && btn.getAttribute('href')) {
          // Anchor: navigate. Closing the panel will happen on page change anyway.
          return;
        }
        e.preventDefault();
        if (openKey === btn.dataset.dkMega) close();
        else open(btn.dataset.dkMega);
      });
    });

    panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    panel.addEventListener('mouseleave', () => { closeTimer = setTimeout(close, 120); });
    const navRoot = $('.dk-catnav');
    if (navRoot) {
      navRoot.addEventListener('mouseleave', () => { closeTimer = setTimeout(close, 120); });
    }
    document.addEventListener('click', (e) => {
      if (!openKey) return;
      if (panel.contains(e.target)) return;
      if (e.target.closest('[data-dk-mega]')) return;
      close();
    });
  }

  // -------------------------------------------------------- Promo bar carousel
  function initPromoBar() {
    const host = $('.dk-promobar') || $('[data-dk-promobar]');
    if (!host) return;

    const messages = Array.from(host.querySelectorAll('.dk-promobar-message'));
    const dots = Array.from(host.querySelectorAll('[data-dk-promobar-dots] .dot'));
    if (!messages.length) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let i = 0;
    let timer;
    let hovered = false;
    let focused = false;

    const activate = (index) => {
      i = index;
      messages.forEach((message, idx) => {
        message.classList.toggle('active', idx === i);
        message.setAttribute('aria-hidden', String(idx !== i));
      });
      dots.forEach((dot, idx) => dot.classList.toggle('active', idx === i));
    };
    const syncTimer = () => {
      clearInterval(timer);
      if (motion.matches) activate(0);
      if (messages.length > 1 && !motion.matches && !document.hidden && !hovered && !focused) {
        timer = setInterval(() => activate((i + 1) % messages.length), 6000);
      }
    };
    host.addEventListener('pointerenter', () => { hovered = true; syncTimer(); });
    host.addEventListener('pointerleave', () => { hovered = false; syncTimer(); });
    host.addEventListener('focusin', () => { focused = true; syncTimer(); });
    host.addEventListener('focusout', (event) => {
      focused = host.contains(event.relatedTarget);
      syncTimer();
    });
    document.addEventListener('visibilitychange', syncTimer);
    motion.addEventListener('change', syncTimer);
    activate(0);
    syncTimer();

    const closeBtn = host.querySelector('[data-dk-promobar-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', syncTimer);
      motion.removeEventListener('change', syncTimer);
      host.remove();
    });
  }

  // -------------------------------------------------------- Mobile nav drawer
  function mobileNavHTML() {
    const catLinks = $$('.dk-catnav a, .dk-catnav [data-dk-mega]').map((el) => {
      const href = el.getAttribute('href') || '#';
      const text = (el.textContent || '').trim();
      return { href, text };
    }).filter((l) => l.text);
    const utilLinks = $$('.dk-utility a').map((el) => ({
      href: el.getAttribute('href') || '#',
      text: (el.textContent || '').trim(),
    })).filter((l) => l.text);

    return `
      <div class="dk-drawer-backdrop" data-dk-mobile-nav-close></div>
      <aside class="dk-mobile-nav" role="dialog" aria-label="Menu" aria-modal="true">
        <div class="dk-drawer-header">
          <div class="dk-drawer-title">Menu</div>
          <button type="button" class="dk-drawer-close" data-dk-mobile-nav-close aria-label="Close menu">
            ${I('x', 'width="18" height="18"')}
          </button>
        </div>
        <div class="dk-mobile-nav-body">
          <h6>Shop</h6>
          <ul class="dk-mobile-nav-list">
            ${catLinks.map((l) => `<li><a href="${escapeHtml(l.href)}">${escapeHtml(l.text)}</a></li>`).join('')}
          </ul>
          ${utilLinks.length ? `
          <h6>Account &amp; support</h6>
          <ul class="dk-mobile-nav-list">
            ${utilLinks.map((l) => `<li><a href="${escapeHtml(l.href)}">${escapeHtml(l.text)}</a></li>`).join('')}
          </ul>` : ''}
        </div>
      </aside>`;
  }

  DK.openMobileNav = function () {
    // Operate on the Liquid-rendered <aside class="dk-mobile-nav"> — not a separate JS host.
    const nav = $('.dk-mobile-nav');
    if (!nav) return;
    const body = nav.querySelector('[data-dk-mobile-nav-body]');
    if (body && !body.dataset.populated) {
      // Populate ONCE by mirroring the desktop catnav + utility links.
      const catLinks = $$('.dk-catnav a, .dk-catnav [data-dk-mega]').map((el) => ({
        href: el.getAttribute('href') || '#',
        text: (el.textContent || '').trim(),
      })).filter((l) => l.text && l.href !== '#');
      const utilLinks = $$('.dk-utility a').map((el) => ({
        href: el.getAttribute('href') || '#',
        text: (el.textContent || '').trim(),
      })).filter((l) => l.text);
      body.innerHTML = `
        <h6 class="dk-mobile-nav__h">Shop</h6>
        ${catLinks.map((l) => `<a href="${escapeHtml(l.href)}">${escapeHtml(l.text)}</a>`).join('')}
        ${utilLinks.length ? `<h6 class="dk-mobile-nav__h">Account &amp; support</h6>` : ''}
        ${utilLinks.map((l) => `<a href="${escapeHtml(l.href)}">${escapeHtml(l.text)}</a>`).join('')}
      `;
      body.dataset.populated = '1';
    }
    requestAnimationFrame(() => {
      nav.classList.add('open');
      nav.setAttribute('aria-hidden', 'false');
      lockScroll(true);
    });
  };
  DK.closeMobileNav = function () {
    const nav = $('.dk-mobile-nav');
    if (!nav) return;
    nav.classList.remove('open');
    nav.setAttribute('aria-hidden', 'true');
    lockScroll(false);
  };

  // -------------------------------------------------------- Wishlist
  const WISH_KEY = 'dk:wish';
  const WISH_META_KEY = 'dk:wish-meta'; // { [id]: handle } — lets the wishlist page hydrate cards
  function readWish() {
    try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch (e) { return []; }
  }
  function writeWish(arr) {
    try { localStorage.setItem(WISH_KEY, JSON.stringify(arr)); } catch (e) { /* noop */ }
  }
  function readWishMeta() {
    try { return JSON.parse(localStorage.getItem(WISH_META_KEY)) || {}; } catch (e) { return {}; }
  }
  function writeWishMeta(obj) {
    try { localStorage.setItem(WISH_META_KEY, JSON.stringify(obj)); } catch (e) { /* noop */ }
  }
  DK.wishlist = {
    items() { return readWish(); },
    meta() { return readWishMeta(); },
    has(id) { return readWish().includes(String(id)); },
    toggle(id, handle) {
      if (!id) return false;
      const key = String(id);
      const list = readWish();
      const meta = readWishMeta();
      const idx = list.indexOf(key);
      let added;
      if (idx >= 0) { list.splice(idx, 1); added = false; delete meta[key]; }
      else { list.push(key); added = true; if (handle) meta[key] = String(handle); }
      writeWish(list);
      writeWishMeta(meta);
      // sync icons
      $$(`[data-dk-wish="${CSS.escape(key)}"]`).forEach((el) => el.classList.toggle('active', added));
      DK.toast(added ? 'Saved to wishlist' : 'Removed from wishlist', '');
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { id: key, handle: handle || null, added, items: readWish() } }));
      return added;
    },
    syncIcons() {
      const list = readWish();
      $$('[data-dk-wish]').forEach((el) => {
        el.classList.toggle('active', list.includes(String(el.dataset.dkWish)));
      });
    },
  };

  // -------------------------------------------------------- Recently viewed
  const RECENT_KEY = 'dk:recent';
  function pushRecent(p) {
    if (!p || !p.id) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { list = []; }
    list = list.filter((x) => x && x.id !== p.id);
    list.unshift(p);
    if (list.length > 12) list = list.slice(0, 12);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) { /* noop */ }
  }
  DK.recentlyViewed = {
    items() {
      try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; }
      catch (e) { return []; }
    },
    clear() { try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* noop */ } },
  };

  // -------------------------------------------------------- Quick view
  function quickViewHTML(p) {
    const img = (p.featured_image || p.image) || (p.images && p.images[0]) || '';
    const quoteRequired = (p.tags || []).includes('Icom quote required');
    const price = quoteRequired ? 'Request pricing' : fmt(p.price);
    const compare = !quoteRequired && p.compare_at_price && p.compare_at_price > p.price ? fmt(p.compare_at_price) : '';
    const variantId = (p.variants && p.variants[0] && (p.variants[0].id || p.variants[0].variant_id))
      || p.id;
    const desc = p.description || '';
    const vendor = p.vendor ? `<div class="dk-qv-vendor">${escapeHtml(p.vendor)}</div>` : '';
    return `
      <div class="dk-modal-backdrop" data-dk-qv-close></div>
      <dialog class="dk-modal dk-qv-modal" open>
        <button type="button" class="dk-modal-close" data-dk-qv-close aria-label="Close">
          ${I('x', 'width="16" height="16"')}
        </button>
        <div class="dk-qv-grid">
          <div class="dk-qv-img">${img ? `<img src="${escapeHtml(img)}" alt="" loading="lazy" />` : ''}</div>
          <div class="dk-qv-body">
            ${vendor}
            <h3>${escapeHtml(p.title || '')}</h3>
            <div class="dk-qv-price">
              <span class="now">${price}</span>
              ${compare ? `<span class="was">${compare}</span>` : ''}
            </div>
            ${desc ? `<div class="dk-qv-desc">${desc}</div>` : ''}
            <div class="dk-qv-cta">
              ${quoteRequired ? '<a class="dk-btn dk-btn-primary dk-btn-lg" href="tel:18005076002">Call for current pricing</a>' : `<button type="button" class="dk-btn dk-btn-primary dk-btn-lg"
                data-dk-add="${escapeHtml(String(variantId))}">Add to cart</button>`}
              <a class="dk-btn dk-btn-secondary dk-btn-lg" href="${escapeHtml(p.url || ('/products/' + (p.handle || '')))}">Full details</a>
            </div>
          </div>
        </div>
      </dialog>`;
  }

  DK.openQuickView = async function (handle) {
    if (!handle) return;
    let host = $('.dk-qv-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'dk-qv-host';
      document.body.appendChild(host);
    }
    host.innerHTML = '<div class="dk-modal-backdrop"></div><div class="dk-modal dk-qv-modal dk-qv-loading">Loading…</div>';
    requestAnimationFrame(() => { host.classList.add('open'); lockScroll(true); });
    try {
      const r = await json('/products/' + encodeURIComponent(handle) + '.js');
      if (!r.ok) throw new Error('not ok');
      const p = await r.json();
      host.innerHTML = quickViewHTML(p);
    } catch (e) {
      host.innerHTML = '<div class="dk-modal-backdrop" data-dk-qv-close></div><div class="dk-modal dk-qv-modal">Could not load product. <button type="button" data-dk-qv-close>Close</button></div>';
    }
  };
  DK.closeQuickView = function () {
    const host = $('.dk-qv-host');
    if (host) host.classList.remove('open');
    lockScroll(false);
  };

  // -------------------------------------------------------- Click delegation
  function initDelegation() {
    document.addEventListener('click', (e) => {
      // Add to cart
      const addBtn = e.target.closest('[data-dk-add]');
      if (addBtn) {
        e.preventDefault();
        const id = addBtn.dataset.dkAdd;
        const qty = Number(addBtn.dataset.qty || addBtn.dataset.dkQty || 1) || 1;
        DK.cart.add(id, qty);
        return;
      }
      // Remove from cart
      const rmBtn = e.target.closest('[data-dk-remove]');
      if (rmBtn) {
        e.preventDefault();
        const line = Number(rmBtn.dataset.dkRemove);
        if (line > 0) DK.cart.remove(line);
        return;
      }
      // Change qty
      const chBtn = e.target.closest('[data-dk-change]');
      if (chBtn) {
        e.preventDefault();
        const line = Number(chBtn.dataset.dkChange);
        const value = Number(chBtn.dataset.value);
        if (line > 0 && !Number.isNaN(value)) DK.cart.change(line, Math.max(0, value));
        return;
      }
      // Quick view
      const qvBtn = e.target.closest('[data-dk-quickview]');
      if (qvBtn) {
        e.preventDefault();
        DK.openQuickView(qvBtn.dataset.dkQuickview);
        return;
      }
      // Wishlist
      const wishBtn = e.target.closest('[data-dk-wish]');
      if (wishBtn) {
        e.preventDefault();
        // Capture handle from parent pcard (or body on PDP) so the wishlist page can hydrate
        const pcard = wishBtn.closest('[data-handle]');
        const handle = (pcard && pcard.dataset.handle) || document.body.getAttribute('data-product-handle') || '';
        DK.wishlist.toggle(wishBtn.dataset.dkWish, handle);
        return;
      }
      // Cart drawer
      if (e.target.closest('[data-dk-cart-open]')) { e.preventDefault(); DK.openCart(); return; }
      if (e.target.closest('[data-dk-cart-close]')) { e.preventDefault(); DK.closeCart(); return; }
      // Search
      if (e.target.closest('[data-dk-search-open]')) { e.preventDefault(); DK.openSearch(); return; }
      if (e.target.closest('[data-dk-search-close]')) { e.preventDefault(); DK.closeSearch(); return; }
      // Mobile nav
      if (e.target.closest('[data-dk-mobile-nav-trigger]')) { e.preventDefault(); DK.openMobileNav(); return; }
      if (e.target.closest('[data-dk-mobile-nav-close]')) { e.preventDefault(); DK.closeMobileNav(); return; }
      // Quick view close
      if (e.target.closest('[data-dk-qv-close]')) { e.preventDefault(); DK.closeQuickView(); return; }
    });

    // Escape closes overlays
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const host = $('.dk-cart-drawer');
      if (host && host.classList.contains('open')) { DK.closeCart(); return; }
      const search = $('.dk-search-overlay');
      if (search && search.classList.contains('open')) { DK.closeSearch(); return; }
      const qv = $('.dk-qv-host');
      if (qv && qv.classList.contains('open')) { DK.closeQuickView(); return; }
      const mn = $('.dk-mobile-nav');
      if (mn && mn.classList.contains('open')) { DK.closeMobileNav(); return; }
      const mega = $('.dk-megamenu');
      if (mega && mega.classList.contains('open')) mega.classList.remove('open');
    });
  }

  // -------------------------------------------------------- Sticky header (passive scroll)
  function initStickyHeader() {
    const header = $('[data-dk-header]') || $('.dk-header');
    if (!header) return;
    let last = 0;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        header.classList.toggle('scrolled', y > 24);
        header.classList.toggle('hide', y > last && y > 240);
        last = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // -------------------------------------------------------- Recently-viewed capture
  function captureCurrentProduct() {
    if (!document.body.classList.contains('template-product')) return;
    const p = window.DK_CURRENT_PRODUCT;
    if (p && p.id) pushRecent(p);
  }

  // -------------------------------------------------------- Lazy hydration helpers
  function lazyHydrate(selector, hydrate) {
    const els = $$(selector);
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => hydrate(el));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          hydrate(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '160px 0px' });
    els.forEach((el) => io.observe(el));
  }

  // -------------------------------------------------------- Boot
  function init() {
    // Cart wiring — keep header in sync any time cart updates.
    DK.cart.on(updateHeaderCart);

    // Delegation + UI
    initDelegation();
    initStickyHeader();
    initMega();
    initPromoBar();
    DK.wishlist.syncIcons();
    captureCurrentProduct();

    // Empire coexistence: if Empire dispatches its own cart events, mirror them.
    // Empire fires events on window.Theme.cart (varies by version). We just refresh ours.
    document.addEventListener('theme:cart:added', () => DK.cart.refresh());
    document.addEventListener('theme:cart:change', () => DK.cart.refresh());

    // Initial cart pull from Shopify
    DK.cart.refresh();

    // Lazy-mount cart drawer host so first-paint is light
    lazyHydrate('[data-dk-lazy-cart]', () => mountCartDrawer());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* dk-deliverto-2026-07-01: interactive delivery-ZIP selector */
(function(){
  function initDeliverTo(){
    var root=document.querySelector('[data-dk-deliverto]'); if(!root||root.__dkInit) return; root.__dkInit=true;
    var btn=root.querySelector('[data-dk-deliverto-toggle]'),
        pop=root.querySelector('[data-dk-deliverto-pop]'),
        zipEl=root.querySelector('[data-dk-deliverto-zip]'),
        form=root.querySelector('[data-dk-deliverto-form]'),
        input=root.querySelector('[data-dk-deliverto-input]'),
        note=root.querySelector('[data-dk-deliverto-note]'),
        DEFNOTE=note?note.textContent:'', KEY='dk_delivery_zip';
    function get(){ try{ return localStorage.getItem(KEY); }catch(e){ return null; } }
    function set(v){ try{ localStorage.setItem(KEY,v); }catch(e){} }
    function render(){ var z=get(); zipEl.textContent = z ? z : 'Set ZIP'; }
    function open(){ pop.hidden=false; btn.setAttribute('aria-expanded','true'); if(note){note.textContent=DEFNOTE; note.classList.remove('is-error');} input.value=get()||''; input.focus(); input.select(); }
    function close(){ pop.hidden=true; btn.setAttribute('aria-expanded','false'); }
    btn.addEventListener('click', function(e){ e.preventDefault(); pop.hidden?open():close(); });
    form.addEventListener('submit', function(e){ e.preventDefault();
      var v=(input.value||'').replace(/[^0-9]/g,'').slice(0,5);
      if(v.length!==5){ if(note){note.textContent='Please enter a valid 5-digit ZIP code.'; note.classList.add('is-error');} input.focus(); return; }
      set(v); render();
      if(note){ note.textContent='Delivering to '+v+' · delivery options and rates are shown at checkout.'; note.classList.remove('is-error'); }
      setTimeout(close, 900);
    });
    input.addEventListener('input', function(){ this.value=this.value.replace(/[^0-9]/g,'').slice(0,5); });
    document.addEventListener('click', function(e){ if(!root.contains(e.target)) close(); });
    document.addEventListener('keydown', function(e){ if(e.key==='Escape') close(); });
    render();
  }
  if(document.readyState==='loading'){ document.addEventListener('DOMContentLoaded', initDeliverTo); } else { initDeliverTo(); }
})();
